import { useEffect, useRef, useState } from "react";

import {
  decideResourceMatch,
  listResourceMatches,
} from "../../../outlets/backoffice/financialGovernanceApi";

const STATUS_OPTIONS = [
  ["candidate", "Candidatos"],
  ["all", "Todos"],
  ["confirmed", "Confirmados"],
  ["rejected", "Rejeitados"],
  ["unmatched", "Sem equivalência"],
];
const SIDE_LABELS = { budget: "Orçamento", actual: "Apropriação" };
const MATCH_LABELS = {
  candidate: "Candidato",
  confirmed: "Confirmado",
  rejected: "Rejeitado",
  unmatched: "Sem equivalência",
};

const safeError = (error) => {
  if (error?.status === 503 && error?.code === "governance_index_unavailable") {
    return "A gravação de equivalências ainda não está habilitada. O índice de governança precisa ser validado em staging.";
  }
  if (error?.status === 409) return error.message || "O candidato foi atualizado. Recarregue e tente novamente.";
  return error?.message || "Não foi possível concluir a revisão de equivalências.";
};

const ResourceMatchReview = ({ companyId, projects = [], onDecisionComplete }) => {
  const activeProjects = projects.filter((project) => project.state === "active");
  const [projectKey, setProjectKey] = useState("");
  const [status, setStatus] = useState("candidate");
  const [payload, setPayload] = useState(null);
  const [reasons, setReasons] = useState({});
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const requestRef = useRef(0);

  const loadMatches = async () => {
    if (!companyId || !projectKey) return null;
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setLoading(true);
    setError(null);
    try {
      const next = await listResourceMatches(companyId, { projectKey, status });
      if (requestRef.current === requestId) setPayload(next);
      return next;
    } catch (requestError) {
      if (requestRef.current === requestId) setError(safeError(requestError));
      return null;
    } finally {
      if (requestRef.current === requestId) setLoading(false);
    }
  };

  useEffect(() => {
    setProjectKey("");
    setPayload(null);
    setReasons({});
    setError(null);
    setNotice(null);
  }, [companyId]);

  useEffect(() => {
    if (projectKey) loadMatches();
  }, [companyId, projectKey, status]);

  const decide = async (item, decision) => {
    const reason = reasons[item.candidateId]?.trim() || "";
    if (reason.length < 10 || busyId) return;
    const identifiers = item.members.map((member) => `${SIDE_LABELS[member.side] || member.side} ${member.erpResourceId}`).join(", ");
    const verb = decision === "confirmed" ? "confirmar" : "rejeitar";
    if (!window.confirm(`Deseja ${verb} a equivalência ${item.canonicalKey}?\n\nIDs: ${identifiers}`)) return;
    setBusyId(item.candidateId);
    setError(null);
    setNotice(null);
    try {
      await decideResourceMatch(companyId, item.candidateId, {
        decision,
        expectedVersion: item.match.decisionVersion,
        candidateRevision: item.candidateRevision,
        reason,
      });
      setReasons((current) => ({ ...current, [item.candidateId]: "" }));
      setNotice(decision === "confirmed" ? "Equivalência confirmada e auditada." : "Equivalência rejeitada e auditada.");
      await loadMatches();
      await onDecisionComplete?.();
    } catch (requestError) {
      if (requestError?.status === 409) await loadMatches();
      setError(safeError(requestError));
    } finally {
      setBusyId(null);
    }
  };

  return <section className="governance-section resource-match-review" aria-labelledby="resource-match-title">
    <header>
      <div><span className="director-eyebrow">Identidade de recursos</span><h2 id="resource-match-title">Revisão de equivalências</h2></div>
      <p>Confirme apenas quando orçamento e apropriação representarem o mesmo recurso.</p>
    </header>
    <div className="governance-grid governance-grid--two">
      <label>Obra para revisar<select aria-label="Obra para revisar" value={projectKey} onChange={(event) => { setProjectKey(event.target.value); setPayload(null); }}>
        <option value="">Selecione</option>
        {activeProjects.map((project) => <option key={project.key} value={project.key}>{project.name}</option>)}
      </select></label>
      <label>Situação<select aria-label="Situação das equivalências" value={status} onChange={(event) => { setStatus(event.target.value); setPayload(null); }}>
        {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
      </select></label>
    </div>

    {error && <div className="director-state director-state--error" role="alert">{error}</div>}
    {notice && <div className="director-state director-state--success" role="status">{notice}</div>}
    {loading && <div className="director-state" role="status">Carregando identidades candidatas…</div>}
    {!projectKey && <p className="resource-match-review__empty">Selecione uma obra para iniciar a revisão.</p>}
    {!loading && projectKey && payload?.status === "no_data" && <p className="resource-match-review__empty">Nenhuma equivalência encontrada neste recorte.</p>}
    {payload?.status === "partial" && <div className="director-quality" role="note">Registros inválidos foram isolados desta lista.</div>}

    <div className="resource-match-review__list">
      {(payload?.items || []).map((item) => {
        const validReason = (reasons[item.candidateId]?.trim().length || 0) >= 10;
        return <article className="resource-match-card" key={item.candidateId}>
          <header>
            <div><span className="director-eyebrow">{item.attributes?.grade || item.normalizedUnit}</span><h3>{item.canonicalKey}</h3></div>
            <div className="item-variance-card__badges"><span className={`item-variance-badge item-variance-badge--${item.match.status}`}>{MATCH_LABELS[item.match.status] || item.match.status}</span><span>Confiança {Math.round(item.match.confidenceBps / 100)}%</span></div>
          </header>
          <dl className="resource-match-attributes">
            {Object.entries(item.attributes || {}).map(([key, value]) => <div key={key}><dt>{key}</dt><dd>{value}</dd></div>)}
            <div><dt>Unidade</dt><dd>{item.normalizedUnit}</dd></div>
            <div><dt>Estratégia</dt><dd>{item.match.strategy}</dd></div>
          </dl>
          <div className="resource-match-members" aria-label={`Membros de ${item.canonicalKey}`}>
            {item.members.map((member) => <div key={`${member.side}-${member.erpResourceId}`}><strong>{SIDE_LABELS[member.side] || member.side} · ID {member.erpResourceId}</strong><span>{member.displayName}</span><small>{member.sourceUnit} → {member.normalizedUnit || "não reconciliada"}</small></div>)}
          </div>
          <label>Motivo da decisão<textarea
            aria-label={`Motivo da decisão de ${item.canonicalKey}`}
            value={reasons[item.candidateId] || ""}
            onChange={(event) => setReasons((current) => ({ ...current, [item.candidateId]: event.target.value }))}
            maxLength="500"
            disabled={Boolean(busyId)}
          /><small>Mínimo de 10 caracteres. A justificativa ficará no histórico.</small></label>
          <div className="resource-match-actions">
            <button type="button" className="governance-remove" disabled={!validReason || Boolean(busyId)} onClick={() => decide(item, "rejected")}>Rejeitar equivalência</button>
            <button type="button" className="director-button" disabled={!validReason || Boolean(busyId)} onClick={() => decide(item, "confirmed")}>Confirmar equivalência</button>
          </div>
        </article>;
      })}
    </div>
  </section>;
};

export default ResourceMatchReview;
